import React, { useEffect, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════════
// METABALLS BACKGROUND - Animated organic shapes
// ═══════════════════════════════════════════════════════════════════

const MetaballsBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size
    const updateSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    updateSize();
    window.addEventListener('resize', updateSize);

    // Metaballs configuration
    const metaballs = [
      { x: 0.3, y: 0.3, vx: 0.0005, vy: 0.0007, radius: 0.15 },
      { x: 0.7, y: 0.5, vx: -0.0006, vy: 0.0005, radius: 0.12 },
      { x: 0.5, y: 0.8, vx: 0.0008, vy: -0.0006, radius: 0.18 },
      { x: 0.2, y: 0.7, vx: -0.0004, vy: -0.0008, radius: 0.10 },
    ];

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create gradient
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        Math.max(canvas.width, canvas.height) / 2
      );

      gradient.addColorStop(0, 'rgba(147, 51, 234, 0.1)'); // Purple
      gradient.addColorStop(0.5, 'rgba(14, 165, 233, 0.08)'); // Blue
      gradient.addColorStop(1, 'rgba(239, 68, 68, 0.05)'); // Red

      // Update and draw metaballs
      metaballs.forEach(ball => {
        // Update position
        ball.x += ball.vx;
        ball.y += ball.vy;

        // Bounce off edges
        if (ball.x <= ball.radius || ball.x >= 1 - ball.radius) ball.vx *= -1;
        if (ball.y <= ball.radius || ball.y >= 1 - ball.radius) ball.vy *= -1;

        // Draw metaball
        const x = ball.x * canvas.width;
        const y = ball.y * canvas.height;
        const radius = ball.radius * Math.min(canvas.width, canvas.height);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.filter = 'blur(40px)';
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', updateSize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
      style={{
        filter: 'blur(80px)',
        opacity: 0.6,
        mixBlendMode: 'screen'
      }}
    />
  );
};

export default MetaballsBackground;